import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronRight, Database } from 'lucide-react';

function SourcesBlock({ sourcesMarkdown }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 rounded-lg border border-border/50 bg-secondary/20 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <Database className="w-3 h-3" />
        Sources & Data
        {open ? <ChevronDown className="w-3 h-3 ml-auto" /> : <ChevronRight className="w-3 h-3 ml-auto" />}
      </button>
      {open && (
        <div className="px-3 pb-2 border-t border-border/30">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="text-[11px] leading-relaxed text-muted-foreground mb-1 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="list-disc list-outside ml-3 space-y-0.5 mt-1">{children}</ul>,
              li: ({ children }) => <li className="text-[11px] leading-relaxed text-muted-foreground">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
            }}
          >
            {sourcesMarkdown}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <h1 className="text-lg font-bold mt-3 mb-1.5">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold mt-2.5 mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold mt-1.5 mb-0.5">{children}</h4>,
  p: ({ children }) => <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-emerald-500/50 pl-3 my-2 text-muted-foreground italic">{children}</blockquote>
  ),
  code: ({ inline, className, children }) => {
    if (inline) {
      return <code className="bg-secondary px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>;
    }
    return (
      <pre className="bg-secondary/80 rounded-lg p-3 my-2 overflow-x-auto">
        <code className="text-xs font-mono leading-relaxed">{children}</code>
      </pre>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded-lg border border-border/50">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-secondary/60">{children}</thead>,
  th: ({ children }) => <th className="px-3 py-1.5 text-left font-semibold text-foreground border-b border-border/50 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-3 py-1.5 border-b border-border/30 whitespace-nowrap">{children}</td>,
  tr: ({ children }) => <tr className="hover:bg-secondary/30">{children}</tr>,
  hr: () => <hr className="my-3 border-border/50" />,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">{children}</a>
  ),
};

function splitSources(content) {
  const pattern = /\n\*\*Sources:\*\*/i;
  const match = content.match(pattern);
  if (!match) return { body: content, sources: null };
  const idx = match.index;
  return {
    body: content.slice(0, idx).trimEnd(),
    sources: content.slice(idx).replace(/^\n/, ''),
  };
}

export default function MarkdownMessage({ content }) {
  if (!content) return null;

  const { body, sources } = useMemo(() => splitSources(content), [content]);

  return (
    <div>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {body}
      </ReactMarkdown>
      {sources && <SourcesBlock sourcesMarkdown={sources} />}
    </div>
  );
}
